import { useState } from "react";
import { useViewer } from "../../../state/ViewerProvider";
import { useUi } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { MiniBtn, Row, TextActionInput } from "../inspector/rows";
import { pickFile } from "../../../util/download";
import { LibraryGrid, type LibItem } from "../library/LibraryGrid";
import { useManifest } from "../library/useManifest";

/** One row of `public/gem-env/manifest.json` (written by scripts/fetch-hdris.mjs). */
export interface GemEnvEntry { id: string; label: string; file: string; thumb: string }

/** Gem environment panel — the separate high-contrast HDR the diamond
 * ray-tracer samples (independent of the metal/scene env). Load by URL/file and
 * rotate. A curated thumbnail grid lands in Phase 2.
 *
 * Loading a gem env decodes an HDR and re-binds it on every diamond material,
 * which takes seconds, so each loader holds a pending state until its RPC
 * resolves and reports a failure inline — previously the button stayed enabled
 * and users queued a second decode on top of the first. */
export function GemEnvPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const [ui] = useUi();
  const [rot, setRot] = useState(0);
  const [active, setActive] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { items: gems, urlFor, thumbFor } = useManifest<GemEnvEntry>("/gem-env/manifest.json", "gemEnv");
  const libItems: LibItem[] = gems.map((g) => ({ id: g.id, label: g.label, thumb: thumbFor(g) }));

  const uploadGemEnv = async () => {
    // pickFile awaits its callback but discards the callback's rejection, so
    // capture the decode failure and re-throw it after the dialog round-trip.
    let failure: unknown;
    await pickFile(".hdr,.exr", async (f) => {
      try {
        await r.loadDiamondEnvFromFile(f);
        setActive(null);
      } catch (e) {
        failure = e;
      }
    });
    if (failure) throw failure;
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2 rounded-lg bg-ed-row p-2.5">
        <Icon name="diamond" size={18} className="text-ed-accent" />
        <span className="text-[12px] leading-snug text-ed-text-2">
          Diamonds ray-trace against this environment for their sparkle. It is a third, separate slot — neither
          the scene environment nor a Metal HDR override reaches the stones, and changing those never changes these.
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Load gem environment</span>
        {/* Returned, not `void`ed: the field then spins for the real fetch and
            keeps the URL on failure instead of clearing as if it had worked. */}
        <TextActionInput
          placeholder="HDR/EXR URL…"
          action="Load"
          onSubmit={(u) => { setError(null); return r.loadDiamondEnvFromUrl(u); }}
        />
        <MiniBtn
          icon="upload"
          busy={uploadBusy}
          onClick={() => {
            setError(null);
            setUploadBusy(true);
            // pickFile settles when the decode finishes, not when the OS dialog
            // opens, so the spinner covers the actual load.
            uploadGemEnv()
              .catch((e) => setError(e instanceof Error && e.message ? e.message : "Failed to load gem environment."))
              .finally(() => setUploadBusy(false));
          }}
        >
          Upload HDR / EXR
        </MiniBtn>
        {error ? <span className="text-[11px] text-ed-red">{error}</span> : null}
      </div>

      <Row label="Rotation" stacked>
        <input
          type="range"
          className="lux w-full"
          min={0}
          max={Math.PI * 2}
          step={0.01}
          value={rot}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            setRot(v);
            void r.setDiamondEnvRotation(v);
          }}
        />
      </Row>
      {libItems.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Gem HDRI library</span>
          <LibraryGrid
            items={libItems}
            activeId={active}
            // Address the HDR by its manifest `file`. `${it.id}.hdr` happens to
            // resolve for today's four assets and would 404 the moment one is
            // renamed or ships as .exr. Returning the promise lets the grid spin
            // the tile and move the check-mark only once the decode landed.
            onSelect={(it) => {
              const entry = gems.find((g) => g.id === it.id);
              if (!entry) return;
              setError(null);
              return r.loadDiamondEnvFromUrl(urlFor(entry)).then(() => setActive(it.id));
            }}
            onUpload={uploadGemEnv}
            uploadAccept=".hdr,.exr"
            view={ui.libraryView}
            disabled={uploadBusy}
          />
          <span className="text-[11px] text-ed-text-3">Poly Haven · CC0</span>
        </div>
      ) : null}
    </div>
  );
}
